import {
  ComponentType,
  FocusEvent,
  KeyboardEvent,
  useRef,
  useState,
} from "react";
import { t } from "@lingui/core/macro";
import { uniqBy, pipe, pluck, prop, unnest } from "ramda";
import { type Document } from "flexsearch";

import { Icon, IconVariant } from "_/components/icon";
import { OrganizationWithRbac } from "_/data/orgs";
import { PermissionTest } from "_/components/permission-test";
import { Permission, permissions } from "_/data/rbac/definitions";

import {
  type DocsIndexDoc,
  type MachinesIndexDoc,
  type ProjectsIndexDoc,
  useDocsIndex,
  useMachinesIndex,
  useProjectsIndex,
} from "./indices";
import {
  SearchResultList,
  MachineSearchResultItem,
  DocSearchResultItem,
  ProjectSearchResultItem,
  SearchResult,
  SearchResultItemProps,
} from "./searches";
import * as S from "./styled";
import { indexById } from "_/utils";

type SearchResultKind = "all" | "projects" | "machines" | "docs";

const SEARCH_CONF = { enrich: true, limit: 100 } as const;

/**
 * Deduplicates and slices a raw FlexSearch enriched result set.
 *
 * Accepts `rawResults` typed as `unknown` because FlexSearch's TypeScript
 * types for enriched results don't precisely match the shape we read. The
 * shape at runtime is `Array<{ result: Array<{ id: string; doc: T }> }>`,
 * one entry per indexed field, which is what this function flattens and deduplicates.
 */
function runSearch<T>(
  query: string,
  index: Document<T, string[]>
): SearchResult<string, T> {
  if (!query || !index) {
    return [];
  }

  // Deduplicate (by document id, since a document can match on several
  // indexed fields) and flatten the results. `uniqBy` on the id is O(n),
  // unlike `uniq` which deep-compares enriched result objects.
  const clean = pipe(pluck("result"), unnest, uniqBy(prop("id")));
  const rawResults = index.search(query, SEARCH_CONF);
  return clean(rawResults) as SearchResult<string, T>;
}

// Number of results shown per section on the "all" tab, before "show more".
const ALL_TAB_PREVIEW_COUNT = 3;

type AllTabSectionProps<T extends SearchTabType> = {
  resultsInfo: SearchResultsInfo<T>;
  query: string;
  onResultClick: () => void;
  onShowMore: (tab: SearchResultKind) => void;
};

// A single section of the "all" tab: a header plus a short preview of results
// for one kind, with a "show more" button when more results are available.
export const AllTabSection = <T extends SearchTabType>({
  resultsInfo,
  query,
  onResultClick,
  onShowMore,
}: AllTabSectionProps<T>) => {
  const subtitle = resultsInfo.count ? null : (
    <S.SectionSubtitle> ({t`search.no-results-short`})</S.SectionSubtitle>
  );
  return (
    <S.AllResultsTabSection>
      <S.SectionHeader>
        <Icon variant={resultsInfo.icon} />
        <S.SectionTitle>
          {resultsInfo.label}
          {subtitle}
        </S.SectionTitle>
        {resultsInfo.count > ALL_TAB_PREVIEW_COUNT && (
          <S.SectionShowMore onClick={() => onShowMore(resultsInfo.id)}>
            {t`search.show-more`}
          </S.SectionShowMore>
        )}
      </S.SectionHeader>
      <S.SectionResults>
        <SearchResultList
          results={resultsInfo.results.slice(0, ALL_TAB_PREVIEW_COUNT)}
          query={query}
          onClick={onResultClick}
          getResultUrl={resultsInfo.getResultUrl}
          resultItemComponent={resultsInfo.resultItemComponent}
        />
      </S.SectionResults>
    </S.AllResultsTabSection>
  );
};

type SearchTabType = MachinesIndexDoc | ProjectsIndexDoc | DocsIndexDoc;

type SearchResultsInfo<T extends SearchTabType> = {
  id: SearchResultKind;
  results: SearchResult<string, T>;
  resultItemComponent: ComponentType<SearchResultItemProps<T>>;
  label: string;
  icon: IconVariant;
  count: number;
  permissions?: Permission | Permission[] | Permission[][];
  getResultUrl: (doc: T) => string;
};

type SearchResultTabContentsProps<T extends SearchTabType> = {
  allResultsInfo: Record<SearchResultKind, SearchResultsInfo<T>>;
  activeResultKind: SearchResultKind;
  query: string;
  onResultClick: () => void;
  onShowMore: (tab: SearchResultKind) => void;
};

// Renders results for a single kind of results, either a limited-length
// preview of results for the "all" tab, or a full tab of results.
const SearchResultTabContents = <T extends SearchTabType>({
  allResultsInfo,
  activeResultKind,
  query,
  onResultClick,
  onShowMore,
}: SearchResultTabContentsProps<T>) => {
  if (activeResultKind === "all") {
    // For the "all" tab, we show a section for each kind of results, with a few
    // results per section and a "show more" button if there are more results.
    return (
      <>
        {Object.values(allResultsInfo).map((resultsInfo) => (
          <PermissionTest
            key={resultsInfo.id}
            requiredPermissions={resultsInfo.permissions}
          >
            <AllTabSection
              resultsInfo={resultsInfo}
              query={query}
              onResultClick={onResultClick}
              onShowMore={onShowMore}
            />
          </PermissionTest>
        ))}
      </>
    );
  }

  // For all other tabs, just show the list of results for that kind,
  // or a "no results" message if there are none.
  const resultsInfo = allResultsInfo[activeResultKind];
  const innerContent = resultsInfo.results.length ? (
    <SearchResultList
      results={resultsInfo.results}
      query={query}
      onClick={onResultClick}
      getResultUrl={resultsInfo.getResultUrl}
      resultItemComponent={resultsInfo.resultItemComponent}
    />
  ) : (
    <S.NoResults>{t`search.no-results`}</S.NoResults>
  );
  return (
    <PermissionTest requiredPermissions={resultsInfo.permissions}>
      {innerContent}
    </PermissionTest>
  );
};

export const SearchBox = ({
  currentOrg,
}: {
  currentOrg: OrganizationWithRbac | null | undefined;
}) => {
  const [query, setQuery] = useState("");
  const [activeTab, setActiveTab] = useState<SearchResultKind>("all");
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const projectsIndex = useProjectsIndex();
  const machinesIndex = useMachinesIndex(currentOrg);
  const docsIndex = useDocsIndex();

  if (!currentOrg) {
    return null;
  }

  const projectsResults = runSearch<ProjectsIndexDoc>(query, projectsIndex);
  const machinesResults = runSearch<MachinesIndexDoc>(query, machinesIndex);
  const docsResults = runSearch<DocsIndexDoc>(query, docsIndex);

  const reset = () => {
    setQuery("");
    setActiveTab("all");
    // Cast is safe because we're testing for presence of `blur`.
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    (document.activeElement as HTMLElement)?.blur?.();
  };

  const handleBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setActiveTab("all");
    }
  };

  const handleKeyDown = ({ key }: KeyboardEvent<HTMLInputElement>) => {
    switch (key) {
      case "Escape":
        // Cast is safe because we're testing for presence of `blur`.
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion
        (document.activeElement as HTMLElement)?.blur?.();
        break;
    }
  };

  const switchTab = (tab: SearchResultKind) => {
    inputRef.current?.focus();
    setActiveTab(tab);
  };

  const machineTabInfo: SearchResultsInfo<MachinesIndexDoc> = {
    id: "machines",
    results: machinesResults,
    resultItemComponent: MachineSearchResultItem,
    label: t`common.machines`,
    icon: "Machines",
    count: machinesResults.length,
    permissions: [permissions.machines.read],
    getResultUrl: (doc) => `/machines/${doc.id}`,
  };

  const projectTabInfo: SearchResultsInfo<ProjectsIndexDoc> = {
    id: "projects",
    results: projectsResults,
    resultItemComponent: ProjectSearchResultItem,
    label: t`common.projects`,
    icon: "Projects",
    count: projectsResults.length,
    permissions: [permissions.projects.read],
    getResultUrl: (doc) => `/projects/${doc.id}`,
  };

  const docsTabInfo: SearchResultsInfo<DocsIndexDoc> = {
    id: "docs",
    results: docsResults,
    resultItemComponent: DocSearchResultItem,
    label: t`common.docs`,
    icon: "Docs",
    count: docsResults.length,
    permissions: undefined,
    getResultUrl: (doc) => `/docs/${doc.slug}`,
  };

  // Not sure how to get TS to type this correctly, and already wasted too much
  // time on it.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const tabs = indexById([
    projectTabInfo,
    machineTabInfo,
    docsTabInfo,
  ]) as Record<SearchResultKind, SearchResultsInfo<SearchTabType>>;

  return (
    <S.Wrapper onBlur={handleBlur}>
      <S.SearchInputWrapper>
        <Icon variant="Search" />
        <S.SearchInput
          ref={inputRef}
          type="text"
          role="combobox"
          name="q"
          value={query}
          autoComplete="off"
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      </S.SearchInputWrapper>
      <S.SearchResults ref={resultsRef}>
        {query !== "" && (
          <>
            <S.TabBar>
              <S.Tab
                $active={activeTab === "all"}
                onClick={() => setActiveTab("all")}
              >
                {t`common.all`}
              </S.Tab>
              {Object.values(tabs).map((tab) => (
                <S.Tab
                  key={tab.id}
                  $active={activeTab === tab.id}
                  $noResults={tab.count === 0}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                  {tab.count > 0 && (
                    <S.TabCount>{` (${tab.count})`}</S.TabCount>
                  )}
                </S.Tab>
              ))}
            </S.TabBar>
            <SearchResultTabContents
              allResultsInfo={tabs}
              activeResultKind={activeTab}
              query={query}
              onResultClick={reset}
              onShowMore={switchTab}
            />
          </>
        )}
      </S.SearchResults>
    </S.Wrapper>
  );
};
